import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_RELAY_UPDATE } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, RelayUpdateLocationItem, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  getRelayUpdateSearchableKeywords,
  MAX_UPDATE_HOUR,
  MIN_UPDATE_HOUR,
  RELAY_UPDATE_DEFAULT_VALUE_FOR_CLOUD,
  RELAY_UPDATE_DEFAULT_VALUE_FOR_ONPREMISE,
} from '../../../../utils/uiSettings/relay/relay.values';
import { RelayUpdateFormValue, RelayUpdateSearchableKeywords } from '../../../../utils/uiSettings/relay/relay.types';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzRelayUpdateLocationsTableWebComponent } from '../gridComponents/GzRelayUpdateLocationsTableWebComponent';
import { classMap } from 'lit/directives/class-map.js';

@customElement(WEB_COMPONENT_POLICY_PANEL_RELAY_UPDATE)
export class GzRelayUpdateWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  RelayUpdateFormValue,
  RelayUpdateSearchableKeywords
> {
  /**
   * Path for Incidents Sensor module.
   */
  protected path = {
    moduleId: ModuleId.RELAY,
    sectionId: SectionId.UPDATE,
  };

  /**
   * Searchable keywords for Relay Update section.
   */
  protected searchableKeywords = getRelayUpdateSearchableKeywords();

  @query('#update-interval-checkbox-form-field')
  private updateIntervalCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#update-interval-input-form-field')
  private updateIntervalInput: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#download-folder-form-field')
  private downloadFolderFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;

  // locations selectors
  @query('#locations-checkbox-form-field')
  private locationsCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#locations-grid-form-field')
  private locationsGridFormField: GzFormFieldWebComponent<
    GzRelayUpdateLocationsTableWebComponent,
    RelayUpdateLocationItem[]
  >;
  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html` <gz-title
      class="content"
      searchable-label="${this.searchableKeywords[RelayUpdateSearchableKeywords.Update].label}"
      ><span>${this.searchableKeywords[RelayUpdateSearchableKeywords.Update].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Define the update settings for target endpoints with the Relay role.')}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="mt-2">
        <div class="checkbox-with-input">
          <gz-form-field class="label-mw-250 checkbox-width" id="update-interval-checkbox-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[RelayUpdateSearchableKeywords.CheckForUpdates].label}"
              label="${translateFn('Check for updates every (hours)') + ':'}"
              gz-form-input
              @change="${e => toggleDisabled(this.updateIntervalInput, !e.detail.checked)}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field class="label-mw-250" id="update-interval-input-form-field">
            <gz-input-number
              gz-form-input
              inputWidth="${InputWidthValues.SMALL}"
              min="${MIN_UPDATE_HOUR}"
              max="${MAX_UPDATE_HOUR}"
            ></gz-input-number>
          </gz-form-field>
        </div>

        <gz-form-field
          searchable-label="${this.searchableKeywords[RelayUpdateSearchableKeywords.DownloadFolder].label}"
          id="download-folder-form-field"
          class="label-mw-250"
          label="${this.searchableKeywords[RelayUpdateSearchableKeywords.DownloadFolder].translatedLabel}"
        >
          <gz-input
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            placeholder="${translateFn('Specify the full path')}"
          ></gz-input>
          <gz-input-error></gz-input-error>
        </gz-form-field>
      </div>

      <div class="section width-100">
        <gz-title
          fontSize="11"
          searchable-label="${this.searchableKeywords[RelayUpdateSearchableKeywords.UpdateLocations].label}"
          fontWeight="600"
          class="form-subtitle ${classMap({ hidden: !Application.Main.product.isCloud() })}"
          >${this.searchableKeywords[RelayUpdateSearchableKeywords.UpdateLocations].translatedLabel}</gz-title
        >

        <gz-form-field
          id="locations-checkbox-form-field"
          class="${classMap({ hidden: Application.Main.product.isCloud() })}"
        >
          <gz-checkbox
            gz-form-input
            searchable-label="${this.searchableKeywords[RelayUpdateSearchableKeywords.UpdateLocations].label}"
            label="${this.searchableKeywords[RelayUpdateSearchableKeywords.UpdateLocations].translatedLabel}"
            class="checkbox-subtitle"
            @change="${e => toggleDisabled(this.locationsGridFormField, !e.detail.checked)}"
          ></gz-checkbox>
        </gz-form-field>

        <gz-form-field id="locations-grid-form-field">
          <gz-policy-panel-relay-update-locations-table gz-form-input></gz-policy-panel-relay-update-locations-table>
        </gz-form-field>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.relay.update
   */
  protected getValueFromManager(): RelayUpdateFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.RELAY]: {
          [SectionId.UPDATE]: Application.Main.product.isCloud()
            ? RELAY_UPDATE_DEFAULT_VALUE_FOR_CLOUD
            : RELAY_UPDATE_DEFAULT_VALUE_FOR_ONPREMISE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.RELAY]: {
          [SectionId.UPDATE]: PolicyPanelManager.getInstance().getPolicy('uiSettings.relay.update'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        relay: {
          update: {
            updateInterval: {
              enable: this.updateIntervalCheckbox,
              hours: this.updateIntervalInput,
            },
            downloadFolder: this.downloadFolderFormField,
            updateLocations: {
              enable: this.locationsCheckboxFormField,
              locations: this.locationsGridFormField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        relay: {
          update: {
            updateInterval: {
              enable: this.valueT.uiSettings.relay.update.updateInterval.enable,
              hours: this.valueT.uiSettings.relay.update.updateInterval.hours,
            },
            downloadFolder: this.valueT.uiSettings.relay.update.downloadFolder,
            updateLocations: {
              enable: this.valueT.uiSettings.relay.update.updateLocations.enable,
              locations: this.valueT.uiSettings.relay.update.updateLocations.locations,
            },
          },
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'relay', 'update'], {});
    }

    return value;
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.locationsGridFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_RELAY_UPDATE: GzRelayUpdateWebComponent;
  }
}
